/**
 * CanvasLayoutFindings (audit R25; spec section 13.4).
 *
 * A display surface for layout validation findings. It renders BOTH kinds the
 * spec defines and keeps them visually distinct:
 *  - `error` findings (LAY001/002/003): these block the save. Shown as danger.
 *  - `warn` findings with a normalization (LAY004): the engine grows the element
 *    to its minimum and REPORTS the change. Shown as a warning that names the
 *    from -> to growth, so a min-size normalization is never silent.
 *
 * The component renders nothing when there are no findings, so a clean canvas
 * shows no surface at all.
 */

import React from "react";
import { Alert, Box, Chip, Stack, Typography } from "@mui/joy";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import type { LayFinding } from "../../utils/canvasLayoutFindings";

export interface CanvasLayoutFindingsProps {
  findings: LayFinding[];
  /** Optional test hook. */
  "data-testid"?: string;
}

function normalizationText(finding: LayFinding): string | null {
  if (!finding.normalization) return null;
  const { field, from, to } = finding.normalization;
  const dimension = field === "columnSpan" ? "width" : "height";
  return `${dimension}: ${from} -> ${to} (applied and reported)`;
}

export const CanvasLayoutFindings: React.FC<CanvasLayoutFindingsProps> = ({
  findings,
  "data-testid": dataTestId,
}) => {
  if (findings.length === 0) return null;

  const errors = findings.filter((f) => f.severity === "error");
  const warnings = findings.filter((f) => f.severity === "warn");

  return (
    <Stack spacing={1} data-testid={dataTestId} sx={{ width: "100%" }}>
      {errors.length > 0 && (
        <Alert
          color="danger"
          variant="soft"
          startDecorator={<ErrorOutlineIcon />}
          data-testid={dataTestId ? `${dataTestId}-errors` : undefined}
        >
          <Box>
            <Typography level="title-sm">
              {errors.length === 1
                ? "1 layout problem blocks saving"
                : `${errors.length} layout problems block saving`}
            </Typography>
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              {errors.map((f, i) => (
                <Stack
                  key={`${f.code}-${f.elementId ?? "canvas"}-${i}`}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                >
                  <Chip size="sm" color="danger" variant="solid">
                    {f.code}
                  </Chip>
                  <Typography level="body-sm">{f.message}</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Alert>
      )}

      {warnings.length > 0 && (
        <Alert
          color="warning"
          variant="soft"
          startDecorator={<WarningAmberIcon />}
          data-testid={dataTestId ? `${dataTestId}-warnings` : undefined}
        >
          <Box>
            <Typography level="title-sm">
              {warnings.length === 1
                ? "1 element was normalized (saved, not blocked)"
                : `${warnings.length} elements were normalized (saved, not blocked)`}
            </Typography>
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              {warnings.map((f, i) => {
                const norm = normalizationText(f);
                return (
                  <Stack
                    key={`${f.code}-${f.elementId ?? "canvas"}-${i}`}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >
                    <Chip size="sm" color="warning" variant="solid">
                      {f.code}
                    </Chip>
                    <Typography level="body-sm">
                      {f.message}
                      {norm ? ` [${norm}]` : ""}
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          </Box>
        </Alert>
      )}
    </Stack>
  );
};
